import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Quote, Star, MapPin, Loader2, MessageSquare, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPO_LABELS = {
  residencial: 'Residencial',
  ambulatorio: 'Ambulatorio',
  online: 'Online',
  profesional: 'Profesional individual',
};

export default function TestimoniosPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/testimonios/public`)
      .then((r) => setItems(r.data.testimonios || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter((t) => (t.tipos_tratamiento || []).includes(filter));
  }, [items, filter]);

  const featured = filtered.filter((t) => t.featured);
  const regular = filtered.filter((t) => !t.featured);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="testimonios-page">
      <title>Testimonios de familias · SinAdicciones.org</title>
      <meta name="description" content="Testimonios reales de familias chilenas que encontraron tratamiento para adicciones a través de SinAdicciones.org. Historias auténticas, recomendaciones y opiniones honestas." />
      <link rel="canonical" href="https://sinadicciones.org/testimonios" />
      <meta property="og:title" content="Testimonios · SinAdicciones.org" />
      <meta property="og:description" content="Historias reales de familias que compararon centros y encontraron el tratamiento correcto." />
      <meta property="og:url" content="https://sinadicciones.org/testimonios" />

      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-800/60">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 via-slate-950 to-slate-950 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 pt-24 pb-16 md:pt-32 md:pb-20">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-teal-400 font-semibold mb-6">
            <MessageSquare className="w-4 h-4" /> Voz de las familias
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight mb-6">
            Testimonios <span className="text-teal-400">reales</span> de familias que ya cotizaron
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-3xl leading-relaxed">
            Opiniones honestas de familias chilenas que buscaron tratamiento a través de SinAdicciones.org. Cada testimonio pasó por moderación y fue publicado con autorización expresa.
          </p>
        </div>
      </section>

      {/* FILTROS */}
      <section className="border-b border-slate-800/60 bg-slate-900/40">
        <div className="max-w-6xl mx-auto px-4 py-5 flex flex-wrap gap-2" data-testid="testimonios-filters">
          {[
            { v: 'all', l: 'Todos' },
            { v: 'residencial', l: 'Residencial' },
            { v: 'ambulatorio', l: 'Ambulatorio' },
            { v: 'online', l: 'Online' },
            { v: 'profesional', l: 'Profesionales' },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === f.v
                  ? 'bg-teal-500 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
              data-testid={`filter-${f.v}`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-6xl mx-auto px-4 py-14 md:py-20">
        {loading ? (
          <div className="text-center py-20"><Loader2 className="w-8 h-8 text-teal-400 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <Quote className="w-12 h-12 text-slate-700 mx-auto mb-4" />
            <p className="text-slate-400">Aún no hay testimonios publicados en esta categoría.</p>
            <Link to="/cotizar" className="inline-block mt-6">
              <Button className="bg-teal-600 hover:bg-teal-700">Solicitar mi cotización</Button>
            </Link>
          </div>
        ) : (
          <>
            {featured.length > 0 && (
              <div className="mb-10">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-400 font-semibold mb-6">
                  <Star className="w-4 h-4 fill-amber-400" /> Destacados
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  {featured.map((t) => (
                    <TestimonioCard key={t.testimonio_id} t={t} featured />
                  ))}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonios-grid">
              {regular.map((t) => (
                <TestimonioCard key={t.testimonio_id} t={t} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* CTA FINAL */}
      <section className="border-t border-slate-800/60 bg-gradient-to-br from-teal-900/20 via-slate-950 to-slate-950">
        <div className="max-w-4xl mx-auto px-4 py-16 md:py-24 text-center" data-testid="testimonios-cta">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            ¿También cotizaste? Cuéntanos tu experiencia
          </h2>
          <p className="text-slate-300 text-base md:text-lg max-w-2xl mx-auto mb-8">
            Si ya recibiste una cotización, revisa tu email. Te enviamos un enlace privado a nuestra encuesta 2 días después de tu solicitud.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/cotizar">
              <Button className="bg-teal-600 hover:bg-teal-700 px-8 py-4 text-base">
                Solicitar cotización <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/casos-de-exito">
              <Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 px-8 py-4 text-base">
                Ver casos de éxito
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TestimonioCard({ t, featured = false }) {
  const tipos = t.tipos_tratamiento || [];
  return (
    <Card
      className={`bg-slate-900 border-slate-800 hover:border-teal-500/40 transition-colors ${featured ? 'ring-1 ring-amber-400/30' : ''}`}
      data-testid={`testimonio-${t.testimonio_id}`}
    >
      <CardContent className="p-6">
        <Quote className="w-7 h-7 text-teal-400 mb-4" />
        <p className="text-slate-200 text-base leading-relaxed mb-5 whitespace-pre-line">
          {t.testimonio_text}
        </p>

        {typeof t.nivel_recomendacion === 'number' && (
          <div className="flex items-center gap-1.5 mb-4">
            <span className="text-amber-400 text-sm font-bold">{t.nivel_recomendacion}/10</span>
            <span className="text-slate-500 text-xs">recomendación</span>
          </div>
        )}

        {tipos.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {tipos.map((tp) => (
              <Badge key={tp} variant="outline" className="text-xs border-teal-500/30 text-teal-300">
                {TIPO_LABELS[tp] || tp}
              </Badge>
            ))}
          </div>
        )}

        <div className="border-t border-slate-800 pt-4 flex items-center justify-between">
          <div>
            <div className="text-white font-semibold text-sm">{t.nombre_display || 'Anónimo'}</div>
            {t.region && (
              <div className="flex items-center gap-1 text-slate-400 text-xs mt-0.5">
                <MapPin className="w-3 h-3" /> {t.region}
              </div>
            )}
          </div>
          {t.centro_contratado && (
            <div className="text-right text-xs text-slate-500 max-w-[60%] truncate">
              → {t.centro_contratado}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
